import { format, addDays, startOfWeek, isSameDay, parseISO } from "date-fns";
import { it } from "date-fns/locale";

export const dateUtils = {
  // Format date for display
  formatDate(date, formatString = "dd/MM/yyyy") {
    return format(date, formatString, { locale: it });
  },

  // Format time
  formatTime(time) {
    return time;
  },

  // Get day of week in Italian
  getDayOfWeekName(dayOfWeek) {
    const days = [
      "Domenica",
      "Lunedì",
      "Martedì",
      "Mercoledì",
      "Giovedì",
      "Venerdì",
      "Sabato",
    ];
    return days[dayOfWeek];
  },

  // Get short day of week in Italian
  getShortDayName(dayOfWeek) {
    const days = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
    return days[dayOfWeek];
  },

  // Get month name in Italian
  getMonthName(date, includeYear = true) {
    const formatString = includeYear ? "MMMM yyyy" : "MMMM";
    return format(date, formatString, { locale: it });
  },

  // Generate time slots
  generateTimeSlots(startTime = "09:00", endTime = "18:00", interval = 60) {
    const slots = [];
    const start = parseTime(startTime);
    const end = parseTime(endTime);

    let current = start;
    while (current < end) {
      const timeString = formatTimeFromMinutes(current);
      const endTimeString = formatTimeFromMinutes(current + interval);

      slots.push({
        start: timeString,
        end: endTimeString,
        label: `${timeString} - ${endTimeString}`,
      });

      current += interval;
    }

    return slots;
  },

  // Get calendar weeks for a month
  getCalendarWeeks(date) {
    // Always start from the first day of the month
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const start = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 }); // Monday
    const weeks = [];

    let currentDay = new Date(start);

    // Generate weeks until we include the last day of the month
    while (true) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        const day = new Date(currentDay);
        week.push({
          date: day,
          dayOfMonth: day.getDate(),
          isCurrentMonth: day.getMonth() === date.getMonth(),
          isToday: isSameDay(day, new Date()),
          dayOfWeek: day.getDay(),
        });
        currentDay.setDate(currentDay.getDate() + 1);
      }
      weeks.push(week);

      // Stop if this week contains the last day of the month
      if (week.some(day =>
        day.date.getTime() >= lastDayOfMonth.getTime() &&
        day.isCurrentMonth
      )) {
        break;
      }
    }

    return weeks;
  },

  // Check if date is in the past
  isPastDate(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  },

  // Check if date is today
  isToday(date) {
    return isSameDay(date, new Date());
  },

  // Get date range for next N days
  getDateRange(days = 30) {
    const start = new Date();
    const end = addDays(start, days);
    return { start, end };
  },

  // Convert date to ISO string for API calls
  toISODateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },

  // Parse ISO date string
  fromISODateString(dateString) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  },

  // Get time difference in minutes
  getTimeDifference(startTime, endTime) {
    const start = parseTime(startTime);
    const end = parseTime(endTime);
    return end - start;
  },

  // Check if time slot overlaps
  timeSlotsOverlap(slot1Start, slot1End, slot2Start, slot2End) {
    const start1 = parseTime(slot1Start);
    const end1 = parseTime(slot1End);
    const start2 = parseTime(slot2Start);
    const end2 = parseTime(slot2End);

    return start1 < end2 && start2 < end1;
  },

  // Sort slots by time
  sortSlotsByTime(slots) {
    return slots.sort((a, b) => {
      if (a.date.getTime() !== b.date.getTime()) {
        return a.date.getTime() - b.date.getTime();
      }
      return parseTime(a.startTime) - parseTime(b.startTime);
    });
  },

  // Group slots by date
  groupSlotsByDate(slots) {
    const grouped = {};

    slots.forEach((slot) => {
      const dateKey = this.toISODateString(slot.date);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(slot);
    });

    return grouped;
  },

  // Calculate slot duration
  calculateDuration(startTime, endTime) {
    const minutes = this.getTimeDifference(startTime, endTime);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h ${remainingMinutes}min`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${remainingMinutes}min`;
    }
  },
};

// Helper functions
function parseTime(timeString) {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

function formatTimeFromMinutes(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

export default dateUtils;